import {
  composeReportingPeriodLabel,
  findMeasurementPeriodForYearMo,
  formatWarehouseMeasurementPeriodLabel,
  normalizeWarehouseMeasurementPeriod,
} from '@act/shared/data-transfer-objects';
import type { Cadence } from '@act/shared/data-transfer-objects';
import type {
  ReportMeasurementPeriodOptionDto,
  ReportReportingMonthOptionDto,
} from '@act/shared/data-transfer-objects';

/** How the JOC measurement period was chosen on monthly/quarterly forms. */
export type MeasurementPeriodMatchStatus =
  | 'auto_matched'
  | 'manual'
  | 'no_match'
  | 'empty';

/**
 * Converts a month picker key to warehouse `YYYYMM`.
 * @param monthKey - `YYYY-MM` value from reporting month options.
 * @returns Six-digit year-month or null when not parseable.
 */
export const monthKeyToYearMo = (monthKey: string): string | null => {
  const [year, month] = monthKey.trim().split('-');
  if (!year || !month || !/^\d{4}$/.test(year) || !/^\d{2}$/.test(month)) {
    return null;
  }
  return `${year}${month}`;
};

/**
 * Finds a JOC measurement period whose end month matches the reporting month.
 * @param cadence - Report cadence.
 * @param endMonthKey - Selected reporting month (`YYYY-MM`).
 * @param measurementPeriodValues - Warehouse measurement period values.
 * @returns Matching measurement period or null.
 */
export const resolveAutoMatchedMeasurementPeriod = (
  cadence: Cadence,
  endMonthKey: string,
  measurementPeriodValues: string[],
): string | null => {
  if (cadence === 'adhoc' || !endMonthKey.trim()) {
    return null;
  }
  const yearMo = monthKeyToYearMo(endMonthKey);
  if (!yearMo) {
    return null;
  }
  return findMeasurementPeriodForYearMo(yearMo, measurementPeriodValues);
};

/**
 * Derives UI status for the JOC measurement period field on monthly/quarterly forms.
 * @param params - Cadence, selected values, and auto-match candidate.
 * @returns Match status for helper copy.
 */
export const deriveMeasurementPeriodMatchStatus = (params: {
  cadence: Cadence;
  endMonthKey: string;
  measurementPeriod: string;
  autoMatchedMeasurementPeriod: string | null;
}): MeasurementPeriodMatchStatus => {
  if (params.cadence === 'adhoc' || !params.endMonthKey.trim()) {
    return 'empty';
  }
  const current = normalizeWarehouseMeasurementPeriod(params.measurementPeriod);
  if (!current) {
    return params.autoMatchedMeasurementPeriod ? 'empty' : 'no_match';
  }
  if (
    params.autoMatchedMeasurementPeriod &&
    current ===
      normalizeWarehouseMeasurementPeriod(params.autoMatchedMeasurementPeriod)
  ) {
    return 'auto_matched';
  }
  if (!params.autoMatchedMeasurementPeriod) {
    return 'no_match';
  }
  return 'manual';
};

/**
 * Returns helper copy explaining auto-match state for the measurement period field.
 * @param status - Derived match status.
 * @param autoMatchedLabel - Human-readable auto-match label when present.
 * @returns Caption lines for the form field.
 */
export const buildMeasurementPeriodMatchHelperLines = (
  status: MeasurementPeriodMatchStatus,
  autoMatchedLabel: string,
): string[] => {
  switch (status) {
    case 'auto_matched':
      return [
        `Auto-matched to ${autoMatchedLabel} from your reporting month.`,
        'VBP Engagement and Quality Measure Projections use this JOC window.',
      ];
    case 'manual':
      return [
        autoMatchedLabel
          ? `Using your selection (auto-match would be ${autoMatchedLabel}).`
          : 'Using your selected JOC measurement period.',
        'VBP Engagement and Quality Measure Projections use this window.',
      ];
    case 'no_match':
      return [
        'No JOC measurement period ends in this reporting month.',
        'Select a window manually for VBP Engagement and Quality Measure Projections.',
      ];
    default:
      return [
        'Select the JOC measurement window for VBP Engagement and Quality Measure Projections.',
      ];
  }
};

/**
 * Ensures the report's stored measurement period appears in the select options.
 * @param options - Warehouse measurement period options from the API.
 * @param storedPeriod - Measurement period saved on the report.
 * @returns Options including the stored value when missing from the API list.
 */
export const withStoredMeasurementPeriodOption = (
  options: ReportMeasurementPeriodOptionDto[],
  storedPeriod: string,
): ReportMeasurementPeriodOptionDto[] => {
  const storedValue = storedPeriod
    ? normalizeWarehouseMeasurementPeriod(storedPeriod)
    : '';
  if (!storedValue || options.some(option => option.value === storedValue)) {
    return options;
  }
  return [
    {
      value: storedValue,
      label: formatWarehouseMeasurementPeriodLabel(storedValue),
    },
    ...options,
  ];
};

/**
 * Ensures the report's stored reporting month appears in the select options.
 * @param options - Warehouse reporting month options from the API.
 * @param monthKey - `YYYY-MM` month key from edit hydration.
 * @returns Options including the stored value when missing from the API list.
 */
export const withStoredReportingMonthOption = (
  options: ReportReportingMonthOptionDto[],
  monthKey: string,
): ReportReportingMonthOptionDto[] => {
  if (!monthKey || options.some(option => option.value === monthKey)) {
    return options;
  }
  return [
    {
      value: monthKey,
      label: composeReportingPeriodLabel('monthly', '', monthKey, ''),
    },
    ...options,
  ];
};
